import {DoppeViewerAppClientServiceBase} from './doppe-viewer-app-client-service-base';
import {asyncCachedValueCreate, asyncExecuteInBackground, PartiallyRequiredNotStrict,} from '@wix/devzai-utils-common';
import {DoppeDtoBymoLinkViewerData} from '../../client-server-common/types/doppe-dto-bymo-link-viewer-data';
import type {pageViewParams} from '@wix/bi-logger-seo-data/v2/types';

type SupportedEventName = 'pageView';

type pageViewParamsWithMandatoryParams = PartiallyRequiredNotStrict<pageViewParams, 'url' | 'ref' | 'msid' | 'vid' | 'vsi'> & { bsi: string };

type BmAnalyticsDashboardEventParams<EVENT_NAME extends SupportedEventName> = 
    EVENT_NAME extends 'pageView' ? pageViewParamsWithMandatoryParams : never;

export class DoppeViewerBmAnalyticsDashboardBiClient extends DoppeViewerAppClientServiceBase {

    private isInitialized = false;
    private reportAnalyticsEvents = true;
    private asyncCachedWixBiWebLogger = asyncCachedValueCreate(async () => {
        const {
            doppeViewerBmAnalyticsDashboardWixWebBiLoggerCreate
        } = await import (/* webpackChunkName: "doppe-viewer-bm-analytics-dashboard-bi-logger" */ '../doppe-viewer-bm-analytics-dashboard-bi-logger/doppe-viewer-bm-analytics-dashboard-bi-logger');

        return {
            logger: doppeViewerBmAnalyticsDashboardWixWebBiLoggerCreate()
        }
    })

    public initialize(
        options: {
            bymoLinkViewerData: DoppeDtoBymoLinkViewerData.WithConnectedPage;
            reportAnalyticsEvents: boolean;
        }
    ) {
        if (this.isInitialized) {
            throw new Error(`DoppeViewerBmAnalyticsDashboardBiClient has already been initialized.`)
        }

        this.isInitialized = true;
        this.reportAnalyticsEvents = options.reportAnalyticsEvents;
    }

    public flushLogs() {

        if (!this.reportAnalyticsEvents) {
            return;
        }

        const cachedWixBiWebLogger = this.asyncCachedWixBiWebLogger.getCachedValue();
        if (cachedWixBiWebLogger) {
            cachedWixBiWebLogger.logger.flush().catch(() => {
            })
        }
    }

    public logEvent<EVENT_NAME extends SupportedEventName>(
        eventName: EVENT_NAME,
        params: BmAnalyticsDashboardEventParams<EVENT_NAME>,
    ) {
        if (!this.reportAnalyticsEvents) {
            return;
        }

        asyncExecuteInBackground(async () => {
            const {
                logger
            } = await this.asyncCachedWixBiWebLogger.getValue();

            const factoryLoaders: Record<SupportedEventName, () => Promise<any>> = {
                // Here we can add more events to the BM Analytics Dashboard bi-logger
                pageView: async () => (await import('@wix/bi-logger-seo-data/v2')).pageView,
            };

            const getFactory = factoryLoaders[eventName];

            if (!getFactory) {
                console.warn(`Event '${eventName}' isn't available on BM Analytics Dashboard bi-logger`)
                return;
            }

            const biEventLogObjectFactory = await getFactory();
            await logger.report(biEventLogObjectFactory({
                ...params
            }));
        }, {
            onError: error => {
                console.warn(`Failed logging BM Analytics Dashboard BI event '${eventName}'`, error)
            }
        });
    }
}